import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Card, CardBody, Badge, Spinner, Alert, Button, Input, Switch, Modal, ModalBody, ModalFooter } from '@components/ui';
import { useCompanySettings } from '@features/company/hooks/useCompany';
import { useCertificates } from '../hooks/useCertificates';
import { certificateService } from '../services/certificateService';
import type { JwtProcessType } from '../types/certificate.types';
import { CheckCircleIcon, CalendarIcon, ExclamationTriangleIcon as ExclamationTriangleIconOutline } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon as ExclamationTriangleIconSolid } from '@heroicons/react/24/solid';
import { useQueryClient } from '@tanstack/react-query';
import { appInsightsService } from '../../../services/appInsights/appInsights.service';
import { getCertificateExpiryStatus, formatExpiryRelative } from '../utils/certificateExpiry';
import type { CertificateExpiryStatus } from '../types/certificate.types';
import { cn } from '@lib/utils/cn';

const EXPIRY_BADGE_VARIANT: Record<CertificateExpiryStatus, 'success' | 'warning' | 'danger'> = {
  green: 'success',
  yellow: 'warning',
  red: 'danger',
};

// Icono del badge "Fecha de Vencimiento": calendario neutral en Verde, pero
// triangulo de advertencia en Amarillo/Rojo (solido en Rojo) para que el
// estado llame la atencion de un vistazo, no solo por el color del badge
// (feedback 2026-08-12).
const EXPIRY_ICON: Record<CertificateExpiryStatus, typeof CalendarIcon> = {
  green: CalendarIcon,
  yellow: ExclamationTriangleIconOutline,
  red: ExclamationTriangleIconSolid,
};

export function FiscalInfoTab() {
  const queryClient = useQueryClient();
  const { data: companyData, isLoading, error } = useCompanySettings();
  const { data: certificatesData, isLoading: isLoadingCerts } = useCertificates();
  const [affiliationPac, setAffiliationPac] = useState(false);
  const [jwtProcessType, setJwtProcessType] = useState<JwtProcessType>('none');
  const [secretJwt, setSecretJwt] = useState('');
  const [jwtError, setJwtError] = useState<string | null>(null);
  const [certificateKey, setCertificateKey] = useState('');

  // Minimum length for a valid JWT/Secret token (usually 100+ chars, but we use 50 to be safe)
  const MIN_JWT_LENGTH = 50;

  const handleJwtChange = (value: string) => {
    setSecretJwt(value);
    if (value && value.length < MIN_JWT_LENGTH) {
      setJwtError(`El JWT debe tener al menos ${MIN_JWT_LENGTH} caracteres (actualmente: ${value.length}). Si no tienes el JWT, desactiva esta opción.`);
    } else {
      setJwtError(null);
    }
  };
  const [authCertFile, setAuthCertFile] = useState<File | null>(null);
  const [sigCertFile, setSigCertFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isFirmatech, setIsFirmatech] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Controla si se muestran los campos de carga de certificados (archivos + contraseña).
  // Igual que en el portal legacy: "Actualizar Certificados y Llave" es el switch que
  // despliega el resto de las opciones de certificados.
  const [showCertificateFields, setShowCertificateFields] = useState(false);
  // El pop-up + banner de vencimiento ya NO se disparan desde esta pestaña --
  // se movieron a AppLayout.tsx para que aparezcan "de entrada" en cualquier
  // pantalla tras el login, no solo si el usuario visita Configuracion >
  // Informacion Fiscal y Certificados (feedback 2026-08-12). Esta pestaña
  // conserva el badge de fecha de vencimiento (abajo, certificateExpiry).

  // Precarga del Secret JWT existente del cliente al activar "Actualizar JWT",
  // igual que el portal legacy (input#jwtText con value="@certificate?.SecretJwt").
  // No sobreescribe lo que el usuario ya haya tecleado si vuelve a togglear.
  useEffect(() => {
    if (jwtProcessType === 'update' && !secretJwt) {
      const existingJwt = certificatesData?.certificate?.secretJwt;
      if (existingJwt) {
        setSecretJwt(existingJwt);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtProcessType]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        Error al cargar la información de la compañía. Por favor, intente nuevamente.
      </Alert>
    );
  }

  if (!companyData) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-8">
            <p className="text-secondary-600 dark:text-secondary-400">
              No hay una compañía seleccionada.
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Check if certificates exist from API or from company data
  const apiCertificate = certificatesData?.certificate;
  const hasCertificates = apiCertificate?.certificateAuth && apiCertificate?.certificateSig;
  const certificateExpiry = getCertificateExpiryStatus(
    apiCertificate?.authCertExpiresAt ?? null,
    apiCertificate?.signCertExpiresAt ?? null
  );

  // Sin certificados configurados la carga es obligatoria; con certificados, solo si se
  // activa "Actualizar Certificados y Llave" (upDateCert del portal legacy).
  const updateCertificates = !hasCertificates || showCertificateFields;

  // Misma validación que el botón Guardar de _CertificateJwt.cshtml: debe haber al menos
  // una opción válida (Afiliación al PAC, Actualizar Certificados o Actualizar JWT) y los
  // certificados solo se exigen si se eligió actualizarlos.
  const getValidationError = (): string | null => {
    if (updateCertificates && (!authCertFile || (!isFirmatech && !sigCertFile) || !certificateKey)) {
      return 'Por favor complete todos los campos requeridos';
    }
    if (jwtProcessType === 'update' && !secretJwt) {
      return 'Por favor ingrese el Secret JWT o cambie la opción de JWT';
    }
    if (jwtProcessType === 'update' && secretJwt && secretJwt.length < MIN_JWT_LENGTH) {
      return `El JWT debe tener al menos ${MIN_JWT_LENGTH} caracteres. Si no tienes el JWT, cambia la opción.`;
    }
    if (!affiliationPac && !updateCertificates && jwtProcessType !== 'update') {
      return 'Debe seleccionar al menos una opción.';
    }
    return null;
  };

  // Opciones seleccionadas para el modal de confirmación y el resumen de resultado,
  // paridad con el modal "Confirmar Opciones" del portal legacy (_CertificateJwt.cshtml).
  const getSelectedOptions = (): Array<{ key: 'pac' | 'certs' | 'jwt'; label: string }> => {
    const options: Array<{ key: 'pac' | 'certs' | 'jwt'; label: string }> = [];
    if (affiliationPac) {
      options.push({ key: 'pac', label: 'Afiliación al PAC' });
    }
    if (updateCertificates) {
      options.push({
        key: 'certs',
        label: hasCertificates
          ? 'Actualizar Certificados de Firma y Autenticación'
          : 'Cargar Certificados de Firma y Autenticación',
      });
    }
    if (jwtProcessType === 'create') {
      options.push({ key: 'jwt', label: 'Crear JWT' });
    } else if (jwtProcessType === 'update') {
      options.push({ key: 'jwt', label: 'Actualizar JWT' });
    }
    return options;
  };

  const handleRequestConfirm = () => {
    const validationError = getValidationError();
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setUploadError(null);
    setShowConfirmModal(true);
  };

  const handleSaveCertificates = async () => {
    setShowConfirmModal(false);

    const validationError = getValidationError();
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    const selectedOptions = getSelectedOptions();

    try {
      setIsUploading(true);
      setUploadError(null);

      const signCertFileToUse = isFirmatech ? authCertFile : sigCertFile; // Si es Firmatech, usar el mismo certificado
      const isAffiliationFlow = affiliationPac || jwtProcessType !== 'none';
      let affiliationResult: any = null;

      if (isAffiliationFlow) {
        affiliationResult = await certificateService.updateCertificateAffiliation({
          authCertFile: updateCertificates ? authCertFile : null,
          signCertFile: updateCertificates ? signCertFileToUse : null,
          certificatePassword: updateCertificates ? certificateKey : undefined,
          updateCertificates,
          affiliationPac,
          onlySignatureCert: isFirmatech,
          jwtProcessType,
          secretJwt: jwtProcessType === 'update' ? secretJwt : null,
        });
      } else {
        // Sin afiliación ni JWT, la validación garantiza que se eligió actualizar certificados.
        await certificateService.uploadCertificates({
          authCertFile: authCertFile!,
          signCertFile: signCertFileToUse!,
          secretJwt: null,
          certificatePassword: certificateKey,
        });
      }

      // Invalidate certificates query to refetch
      await queryClient.invalidateQueries({ queryKey: ['certificates'] });

      // Resumen ✓/✗ por opción: el backend de Aludra (mismo endpoint que el portal
      // legacy, /mdl18/UpdateCertificateAffiliation) puede reportar errores parciales
      // por "Step" (Errors: [{ Step, userMessage }]) aunque la llamada HTTP responda 200.
      // Si esa estructura no viene en la respuesta, se mantiene el mensaje genérico.
      const stepErrors: Array<{ Step?: string; step?: string; userMessage?: string; UserMessage?: string }> =
        Array.isArray(affiliationResult?.Errors) ? affiliationResult.Errors : [];

      if (isAffiliationFlow && Array.isArray(affiliationResult?.Errors)) {
        const summaryItems = selectedOptions.map((option) => {
          const failingError = stepErrors.find((err) => {
            const step = (err.Step || err.step || '').toString();
            if (!step) return false;
            if (option.key === 'pac') return step.includes('Affiliation');
            if (option.key === 'certs') return step.includes('UploadCertificates') || step.includes('Cert');
            if (option.key === 'jwt') return step.includes('Jwt');
            return false;
          });
          return {
            label: option.label,
            success: !failingError,
            message: failingError ? (failingError.userMessage || failingError.UserMessage) : undefined,
          };
        });

        const anyFailed = summaryItems.some((item) => !item.success);
        const listHtml = `<ul style="text-align:left; list-style:none; padding-left:0; margin:0;">${summaryItems
          .map(
            (item) =>
              `<li style="margin-bottom:4px;">${item.success ? '✅' : '❌'} ${item.label}${
                !item.success && item.message ? ` - ${item.message}` : ''
              }</li>`
          )
          .join('')}</ul>`;

        await Swal.fire({
          icon: anyFailed ? 'warning' : 'success',
          title: anyFailed
            ? 'Operación completada con errores'
            : updateCertificates
              ? 'Certificados actualizados exitosamente'
              : 'Operación completada exitosamente',
          html: listHtml,
          confirmButtonColor: anyFailed ? '#f59e0b' : '#10b981',
        });
      } else {
        // Show success alert (mensaje genérico, sin granularidad de la respuesta del backend)
        await Swal.fire({
          icon: 'success',
          title: 'Certificados subidos exitosamente',
          text: 'Los certificados digitales han sido configurados correctamente.',
          confirmButtonColor: '#10b981',
        });
      }

      // Clear form
      setAuthCertFile(null);
      setSigCertFile(null);
      setSecretJwt('');
      setCertificateKey('');
      setAffiliationPac(false);
      setJwtProcessType('none');
    } catch (error: any) {
      appInsightsService.trackException(error, { context: 'src/features/certificates/components/FiscalInfoTab' });

      // Check if it's an A400 validation error
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al subir los certificados';
      setUploadError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Company Info */}
      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
              Información de la Compañía
            </h2>
            <Badge variant={hasCertificates ? 'success' : 'warning'}>
              {hasCertificates ? 'Certificados Configurados' : 'Certificados Pendientes'}
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-secondary-600 dark:text-secondary-400">Razón Social:</span>
              <p className="text-secondary-900 dark:text-white font-medium mt-1">
                {companyData.fullName || companyData.name || '-'}
              </p>
            </div>

            <div>
              <span className="text-secondary-600 dark:text-secondary-400">Nombre Comercial:</span>
              <p className="text-secondary-900 dark:text-white font-medium mt-1">
                {companyData.tradeName || companyData.name || '-'}
              </p>
            </div>

            <div>
              <span className="text-secondary-600 dark:text-secondary-400">RUC:</span>
              <p className="text-secondary-900 dark:text-white font-medium mt-1">
                {companyData.numberId || companyData.ruc || '-'}
              </p>
            </div>

            <div>
              <span className="text-secondary-600 dark:text-secondary-400">DV:</span>
              <p className="text-secondary-900 dark:text-white font-medium mt-1">
                {companyData.dv || '-'}
              </p>
            </div>

            {companyData.taxRegime && (
              <div>
                <span className="text-secondary-600 dark:text-secondary-400">Régimen Fiscal:</span>
                <p className="text-secondary-900 dark:text-white font-medium mt-1">
                  {companyData.taxRegime}
                </p>
              </div>
            )}

            {companyData.economicActivity && (
              <div>
                <span className="text-secondary-600 dark:text-secondary-400">Actividad Económica:</span>
                <p className="text-secondary-900 dark:text-white font-medium mt-1">
                  {companyData.economicActivity}
                </p>
              </div>
            )}

            {companyData.website && (
              <div>
                <span className="text-secondary-600 dark:text-secondary-400">Sitio Web:</span>
                <p className="text-secondary-900 dark:text-white font-medium mt-1">
                  <a
                    href={companyData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    {companyData.website}
                  </a>
                </p>
              </div>
            )}

            <div>
              <span className="text-secondary-600 dark:text-secondary-400">Tipo de Contribuyente:</span>
              {companyData.typeTaxpayer ? (
                <p className="text-secondary-900 dark:text-white font-medium mt-1">
                  {companyData.typeTaxpayer === 1 ? 'Persona Natural' : 'Persona Jurídica'}
                </p>
              ) : (
                <p className="text-warning-600 dark:text-warning-400 text-sm mt-1">
                  No configurado - <a data-testid="certificate-update-taxpayer-link" href="/company/edit" className="underline hover:text-warning-700">actualiza tu tipo de contribuyente</a>
                </p>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Certificates Section */}
      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
              Certificados Digitales
            </h2>
            {apiCertificate?.status !== null && (
              <Badge variant={apiCertificate?.status ? 'success' : 'danger'}>
                {apiCertificate?.status ? 'Activo' : 'Inactivo'}
              </Badge>
            )}
          </div>

          {isLoadingCerts ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : hasCertificates ? (
            /* Show existing certificates */
            <div className="space-y-6">
              <Alert variant="success">
                Los certificados digitales están configurados correctamente.
              </Alert>

              {/* Certificate Names */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircleIcon className="w-5 h-5 text-success-600" />
                    <Badge variant="primary">Autenticación</Badge>
                  </div>
                  <p className="text-sm text-secondary-900 dark:text-white font-mono break-all">
                    {apiCertificate?.certificateAuth}
                  </p>
                </div>

                <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircleIcon className="w-5 h-5 text-success-600" />
                    <Badge variant="primary">Firma</Badge>
                  </div>
                  <p className="text-sm text-secondary-900 dark:text-white font-mono break-all">
                    {apiCertificate?.certificateSig}
                  </p>
                </div>
              </div>

              {/* Secret JWT */}
              {apiCertificate?.secretJwt && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Secret JWT
                  </label>
                  <Input
                    data-testid="certificate-secret-jwt-display-input"
                    type="text"
                    value={apiCertificate.secretJwt}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                    Token secreto para la autenticación JWT
                  </p>
                </div>
              )}

              {/* Certificate Key - Encrypted */}
              {apiCertificate?.certificateKey && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Certificate Key (Encriptada)
                  </label>
                  <Input
                    data-testid="certificate-key-display-input"
                    type="password"
                    value={apiCertificate.certificateKey}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                    Contraseña encriptada del certificado
                  </p>
                </div>
              )}

              {/* Dates */}
              {(apiCertificate?.createdDate || apiCertificate?.lastModifiedDate || certificateExpiry.nearestExpiresAt) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                  {apiCertificate?.createdDate && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                        Fecha de Creación
                      </label>
                      <Badge variant="success">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {new Date(apiCertificate.createdDate).toLocaleString('es-PA', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Badge>
                    </div>
                  )}

                  {apiCertificate?.lastModifiedDate && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                        Última Modificación
                      </label>
                      <Badge variant="success">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {new Date(apiCertificate.lastModifiedDate).toLocaleString('es-PA', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Badge>
                    </div>
                  )}

                  {certificateExpiry.nearestExpiresAt && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                        Fecha de Vencimiento
                      </label>
                      {(() => {
                        const ExpiryIcon = EXPIRY_ICON[certificateExpiry.status];
                        return (
                          <Badge variant={EXPIRY_BADGE_VARIANT[certificateExpiry.status]}>
                            <ExpiryIcon className="w-4 h-4 mr-2" />
                            {certificateExpiry.nearestExpiresAt.toLocaleString('es-PA', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Badge>
                        );
                      })()}
                      {certificateExpiry.status !== 'green' && (
                        <p
                          className={cn(
                            'mt-1.5 text-xs font-semibold',
                            certificateExpiry.status === 'red'
                              ? 'text-danger-600 dark:text-danger-400'
                              : 'text-warning-600 dark:text-warning-400'
                          )}
                          data-testid="certificate-expiry-badge-relative"
                        >
                          {formatExpiryRelative(certificateExpiry.status, certificateExpiry.nearestExpiresAt)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Upload new certificates section */}
              <div className="pt-6 border-t border-secondary-200 dark:border-secondary-700">
                <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">
                  Actualizar Certificados
                </h3>
                <div className="space-y-6">
                  {/* Afiliación al PAC Switch */}
                  <div className="flex items-center justify-between p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-secondary-900 dark:text-white">
                        Afiliación al PAC
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        Activa esta opción para afiliar la compañía al PAC (DGI) al actualizar los certificados
                      </p>
                    </div>
                    <Switch
                      data-testid="certificate-affiliation-pac-switch"
                      checked={affiliationPac}
                      onChange={setAffiliationPac}
                      size="sm"
                    />
                  </div>

                  {/* Actualizar Certificados y Llave Switch - despliega los campos de carga */}
                  <div className="p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-secondary-900 dark:text-white">
                          Actualizar Certificados y Llave
                        </p>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">
                          Activa esta opción para cargar nuevos certificados de firma y autorización
                        </p>
                      </div>
                      <Switch
                        data-testid="certificate-update-fields-switch"
                        checked={showCertificateFields}
                        onChange={setShowCertificateFields}
                        size="sm"
                      />
                    </div>

                    {showCertificateFields && (
                      <div className="flex items-center justify-between pl-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                        <div>
                          <p className="text-sm font-medium text-secondary-900 dark:text-white">
                            Solo certificado de firma
                          </p>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400">
                            Si está marcado, solo necesita cargar el certificado de firma. Se usará automáticamente para ambos propósitos.
                          </p>
                        </div>
                        <Switch
                          data-testid="certificate-firmatech-switch"
                          checked={isFirmatech}
                          onChange={(checked) => {
                            setIsFirmatech(checked);
                            if (checked) {
                              setSigCertFile(null); // Limpiar el segundo input si se activa
                            }
                          }}
                          size="sm"
                        />
                      </div>
                    )}
                  </div>

                  {showCertificateFields && (
                    <>
                      <div className={`grid grid-cols-1 ${!isFirmatech ? 'md:grid-cols-2' : ''} gap-6`}>
                        {/* Certificate Sig Upload - Solo si NO es Firmatech */}
                        {!isFirmatech && (
                          <div>
                            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                              Certificado de Firma *
                            </label>
                            {apiCertificate?.certificateSig && (
                              <Badge variant="success" className="mb-2">Cargado</Badge>
                            )}
                            <div className="border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg p-6">
                              <input
                                data-testid="certificate-sig-cert-update-input"
                                type="file"
                                accept=".pfx,.p12"
                                onChange={(e) => setSigCertFile(e.target.files?.[0] || null)}
                                className="hidden"
                                id="sigCertUpdate"
                              />
                              <label htmlFor="sigCertUpdate" className="flex flex-col items-center cursor-pointer">
                                <svg
                                  className="w-12 h-12 text-secondary-400 mb-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                  />
                                </svg>
                                <span className="text-sm text-secondary-600 dark:text-secondary-400">
                                  {sigCertFile ? sigCertFile.name : 'Suelta los archivos aquí o haz clic para subirlos.'}
                                </span>
                                <span className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                                  (Coloque el certificado de firma)
                                </span>
                              </label>
                            </div>
                          </div>
                        )}

                        {/* Certificate Auth Upload */}
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                            {isFirmatech ? 'Certificado Firmatech (Autenticación y Firma) *' : 'Certificado de Autorización *'}
                          </label>
                          {apiCertificate?.certificateAuth && (
                            <Badge variant="success" className="mb-2">Cargado</Badge>
                          )}
                          <div className="border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg p-6">
                            <input
                              data-testid="certificate-auth-cert-update-input"
                              type="file"
                              accept=".pfx,.p12"
                              onChange={(e) => setAuthCertFile(e.target.files?.[0] || null)}
                              className="hidden"
                              id="authCertUpdate"
                            />
                            <label
                              htmlFor="authCertUpdate"
                              className="flex flex-col items-center cursor-pointer"
                            >
                              <svg
                                className="w-12 h-12 text-secondary-400 mb-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                              </svg>
                              <span className="text-sm text-secondary-600 dark:text-secondary-400">
                                {authCertFile ? authCertFile.name : 'Suelta los archivos aquí o haz clic para subirlos.'}
                              </span>
                              <span className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                                (Coloque el certificado de autorización)
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Certificate Key for Update */}
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                          Contraseña de certificados *
                        </label>
                        <Input
                          data-testid="certificate-key-update-input"
                          type="password"
                          autoComplete="new-password"
                          value={certificateKey}
                          onChange={(e) => setCertificateKey(e.target.value)}
                          placeholder="Ingrese la contraseña del certificado"
                        />
                        <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                          Contraseña para desencriptar los certificados digitales
                        </p>
                      </div>
                    </>
                  )}

                  {/* Crear/Actualizar JWT - mutuamente excluyentes */}
                  <div className="flex items-center justify-between p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-secondary-900 dark:text-white">
                        Crear JWT
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        Solicita al PAC la creación de un nuevo Secret JWT
                      </p>
                    </div>
                    <Switch
                      data-testid="certificate-create-jwt-switch"
                      checked={jwtProcessType === 'create'}
                      onChange={(checked) => setJwtProcessType(checked ? 'create' : 'none')}
                      size="sm"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-secondary-900 dark:text-white">
                        Actualizar JWT
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        Actualiza el Secret JWT existente con uno proporcionado por la DGI
                      </p>
                    </div>
                    <Switch
                      data-testid="certificate-update-jwt-switch"
                      checked={jwtProcessType === 'update'}
                      onChange={(checked) => setJwtProcessType(checked ? 'update' : 'none')}
                      size="sm"
                    />
                  </div>

                  {jwtProcessType === 'update' && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                        Secret JWT
                      </label>
                      <Input
                        data-testid="certificate-secret-jwt-update-input"
                        type="text"
                        autoComplete="off"
                        value={secretJwt}
                        onChange={(e) => handleJwtChange(e.target.value)}
                        placeholder="Ingrese el Secret JWT (mínimo 50 caracteres)"
                        className={`font-mono text-xs ${jwtError ? 'border-danger-500' : ''}`}
                      />
                      {jwtError && (
                        <p data-testid="certificate-jwt-error-update" className="mt-1 text-xs text-danger-500">
                          {jwtError}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                        El JWT es un token largo (100+ caracteres) que luce así: 3A88078974CD8166E06349D1950A5FE5...
                      </p>
                    </div>
                  )}

                  {/* Upload Error */}
                  {uploadError && (
                    <Alert data-testid="certificate-update-error" variant="danger">
                      {uploadError}
                    </Alert>
                  )}

                  {/* Update Button */}
                  <div className="flex justify-end">
                    <Button
                      data-testid="certificate-update-button"
                      variant="primary"
                      onClick={handleRequestConfirm}
                      disabled={!!getValidationError() || (jwtProcessType === 'update' && !!jwtError) || isUploading}
                      isLoading={isUploading}
                    >
                      {isUploading ? 'Actualizando...' : 'Actualizar Certificados'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Show upload form - No certificates configured */
            <div className="space-y-6">
              <Alert variant="warning">
                No hay certificados digitales configurados. Por favor, suba los certificados
                requeridos.
              </Alert>

              {/* Firmatech Switch */}
              <div className="flex items-center justify-between p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-secondary-900 dark:text-white">
                    Certificado Firmatech
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    Activa esta opción si tienes un único certificado de Firmatech
                  </p>
                </div>
                <Switch
                  data-testid="certificate-initial-firmatech-switch"
                  checked={isFirmatech}
                  onChange={(checked) => {
                    setIsFirmatech(checked);
                    if (checked) {
                      setSigCertFile(null);
                    }
                  }}
                  size="sm"
                />
              </div>

              {/* Afiliación al PAC Switch */}
              <div className="flex items-center justify-between p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-secondary-900 dark:text-white">
                    Afiliación al PAC
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    Activa esta opción para afiliar la compañía al PAC (DGI) al configurar los certificados
                  </p>
                </div>
                <Switch
                  data-testid="certificate-initial-affiliation-pac-switch"
                  checked={affiliationPac}
                  onChange={setAffiliationPac}
                  size="sm"
                />
              </div>

              {/* Crear/Actualizar JWT - mutuamente excluyentes */}
              <div className="flex items-center justify-between p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-secondary-900 dark:text-white">
                    Crear JWT
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    Solicita al PAC la creación de un nuevo Secret JWT
                  </p>
                </div>
                <Switch
                  data-testid="certificate-initial-create-jwt-switch"
                  checked={jwtProcessType === 'create'}
                  onChange={(checked) => setJwtProcessType(checked ? 'create' : 'none')}
                  size="sm"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-secondary-900 dark:text-white">
                    Actualizar JWT
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    Actualiza el Secret JWT existente con uno proporcionado por la DGI
                  </p>
                </div>
                <Switch
                  data-testid="certificate-initial-update-jwt-switch"
                  checked={jwtProcessType === 'update'}
                  onChange={(checked) => setJwtProcessType(checked ? 'update' : 'none')}
                  size="sm"
                />
              </div>

              <div className={`grid grid-cols-1 ${!isFirmatech ? 'md:grid-cols-2' : ''} gap-6`}>
                {/* Certificate Auth Upload */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    {isFirmatech ? 'Certificado Firmatech (Autenticación y Firma) *' : 'Certificado de Autenticación *'}
                  </label>
                  <div className="border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg p-6">
                    <input
                      data-testid="certificate-auth-cert-input"
                      type="file"
                      accept=".pfx,.p12"
                      onChange={(e) => setAuthCertFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="authCert"
                    />
                    <label
                      htmlFor="authCert"
                      className="flex flex-col items-center cursor-pointer"
                    >
                      <svg
                        className="w-12 h-12 text-secondary-400 mb-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <span className="text-sm text-secondary-600 dark:text-secondary-400">
                        {authCertFile ? authCertFile.name : 'Click para seleccionar archivo'}
                      </span>
                      <span className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                        .pfx o .p12
                      </span>
                    </label>
                  </div>
                </div>

                {/* Certificate Sig Upload - Solo si NO es Firmatech */}
                {!isFirmatech && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                      Certificado de Firma *
                    </label>
                    <div className="border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg p-6">
                      <input
                        data-testid="certificate-sig-cert-input"
                        type="file"
                        accept=".pfx,.p12"
                        onChange={(e) => setSigCertFile(e.target.files?.[0] || null)}
                        className="hidden"
                        id="sigCert"
                      />
                      <label htmlFor="sigCert" className="flex flex-col items-center cursor-pointer">
                        <svg
                          className="w-12 h-12 text-secondary-400 mb-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <span className="text-sm text-secondary-600 dark:text-secondary-400">
                          {sigCertFile ? sigCertFile.name : 'Click para seleccionar archivo'}
                        </span>
                        <span className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                          .pfx o .p12
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Secret JWT and Certificate Key */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-secondary-900 dark:text-white">
                  Configuración de Certificados
                </h3>

                {jwtProcessType === 'update' && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                      Secret JWT
                    </label>
                    <Input
                      data-testid="certificate-secret-jwt-input"
                      type="text"
                      autoComplete="off"
                      value={secretJwt}
                      onChange={(e) => handleJwtChange(e.target.value)}
                      placeholder="Ingrese el Secret JWT (mínimo 50 caracteres)"
                      className={`font-mono text-xs ${jwtError ? 'border-danger-500' : ''}`}
                    />
                    {jwtError && (
                      <p data-testid="certificate-jwt-error" className="mt-1 text-xs text-danger-500">
                        {jwtError}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                      El JWT es un token largo (100+ caracteres) que luce así: 3A88078974CD8166E06349D1950A5FE5...
                    </p>
                  </div>
                )}

                {jwtProcessType !== 'update' && (
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    El JWT no es obligatorio por el momento. Solo actívalo (Crear o Actualizar) si la DGI te lo requiere.
                  </p>
                )}

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Certificate Key (Password) *
                  </label>
                  <Input
                    data-testid="certificate-key-input"
                    type="password"
                    autoComplete="new-password"
                    value={certificateKey}
                    onChange={(e) => setCertificateKey(e.target.value)}
                    placeholder="Ingrese la contraseña del certificado"
                  />
                  <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                    Contraseña para desencriptar los certificados digitales
                  </p>
                </div>
              </div>

              {/* Upload Error */}
              {uploadError && (
                <Alert data-testid="certificate-upload-error" variant="danger">
                  {uploadError}
                </Alert>
              )}

              {/* Save Button */}
              <div className="flex justify-end">
                <Button
                  data-testid="certificate-save-button"
                  variant="primary"
                  onClick={handleRequestConfirm}
                  disabled={!authCertFile || (!isFirmatech && !sigCertFile) || !certificateKey || (jwtProcessType === 'update' && (!secretJwt || !!jwtError)) || isUploading}
                  isLoading={isUploading}
                >
                  {isUploading ? 'Subiendo...' : 'Guardar Certificados'}
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Fiscal Information Section */}
      <Card>
        <CardBody>
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-6">
            Información Fiscal
          </h2>
          <p className="text-secondary-600 dark:text-secondary-400">
            Información fiscal y configuraciones relacionadas con facturación electrónica.
          </p>
          {/* TODO: Add fiscal information fields */}
        </CardBody>
      </Card>

      {/* Modal de confirmación de opciones - paridad con el portal legacy */}
      <Modal
        data-testid="certificate-confirm-options-modal"
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirmar Opciones"
        size="sm"
      >
        <ModalBody className="h-auto">
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-3">
            Opciones seleccionadas:
          </p>
          <ul className="space-y-2">
            {getSelectedOptions().map((option) => (
              <li
                key={option.key}
                className="flex items-center gap-2 text-sm text-secondary-900 dark:text-white"
              >
                <CheckCircleIcon className="w-4 h-4 text-success-600 flex-shrink-0" />
                {option.label}
              </li>
            ))}
          </ul>
        </ModalBody>
        <ModalFooter>
          <Button
            data-testid="certificate-confirm-cancel-button"
            variant="outline"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancelar
          </Button>
          <Button
            data-testid="certificate-confirm-accept-button"
            variant="primary"
            onClick={handleSaveCertificates}
            isLoading={isUploading}
          >
            Confirmar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
